<ejs-dialog #detailDialog
  [visible]="visible"
  [isModal]="true"
  [showCloseIcon]="true"
  cssClass="modal-lg"
  (close)="close()">
  <ng-template #header>{{ 'i18n.domain.analytics.trip-overview.detail-dialog-title' | translate }}</ng-template>
  <ng-template #content>
    @if (tripDetail) {
      <div class="trip-detail-content">

        <section class="mb-4">
          <h6 class="text-uppercase text-body-secondary small mb-3">{{ 'i18n.domain.analytics.trip-overview.detail.section-overview' | translate }}</h6>
          <div class="row g-3">
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.columns.vehicle-id' | translate }}</div>
              <div class="fw-semibold">{{ tripDetail.vehicleId }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.columns.start' | translate }}</div>
              <div class="fw-semibold">{{ formatDateTime(tripDetail.startUtc) }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.columns.end' | translate }}</div>
              <div class="fw-semibold">{{ formatDateTime(tripDetail.endUtc) }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.columns.status' | translate }}</div>
              <div>
                <span class="badge" [class]="statusBadgeClass(tripDetail.status)">{{ formatTripStatus(tripDetail.status) }}</span>
                @if (tripDetail.closedReason != null) {
                  <span class="badge text-bg-secondary ms-1">{{ formatTripClosedReason(tripDetail.closedReason) }}</span>
                }
              </div>
            </div>
          </div>
        </section>

        <section class="mb-4">
          <h6 class="text-uppercase text-body-secondary small mb-3">{{ 'i18n.domain.analytics.trip-overview.detail.section-speed' | translate }}</h6>
          <div class="row g-3">
            <div class="col-12 col-md-6">
              <div class="d-flex justify-content-between align-items-center">
                <span class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.average-speed' | translate }}</span>
                @if (isUnavailable(tripDetail.averageSpeedKmh)) {
                  <span class="badge text-bg-secondary" [title]="unavailableTooltip(tripDetail.averageSpeedKmh)">{{ 'i18n.domain.analytics.trip-overview.detail.unavailable' | translate }}</span>
                } @else {
                  <span class="fw-semibold">{{ formatNumber(statValue(tripDetail.averageSpeedKmh), 'km/h') }}</span>
                }
              </div>
              <div class="progress" role="progressbar" [attr.aria-valuenow]="speedPercent(statValue(tripDetail.averageSpeedKmh))" aria-valuemin="0" aria-valuemax="100">
                <div class="progress-bar bg-primary" [style.width.%]="speedPercent(statValue(tripDetail.averageSpeedKmh))"></div>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="d-flex justify-content-between align-items-center">
                <span class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.maximum-speed' | translate }}</span>
                @if (isUnavailable(tripDetail.maximumSpeedKmh)) {
                  <span class="badge text-bg-secondary" [title]="unavailableTooltip(tripDetail.maximumSpeedKmh)">{{ 'i18n.domain.analytics.trip-overview.detail.unavailable' | translate }}</span>
                } @else {
                  <span class="fw-semibold">{{ formatNumber(statValue(tripDetail.maximumSpeedKmh), 'km/h') }}</span>
                }
              </div>
              <div class="progress" role="progressbar" [attr.aria-valuenow]="speedPercent(statValue(tripDetail.maximumSpeedKmh))" aria-valuemin="0" aria-valuemax="100">
                <div class="progress-bar bg-primary" [style.width.%]="speedPercent(statValue(tripDetail.maximumSpeedKmh))"></div>
              </div>
            </div>
          </div>
        </section>

        <section class="mb-4">
          <h6 class="text-uppercase text-body-secondary small mb-3">{{ 'i18n.domain.analytics.trip-overview.detail.section-distance-fuel' | translate }}</h6>
          <div class="row g-3">
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.columns.distance' | translate }}</div>
              <div class="fw-semibold">{{ formatNumber(tripDetail.accumulatedDistanceKm, 'km', 2) }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.columns.fuel' | translate }}</div>
              <div class="fw-semibold">{{ formatNumber(tripDetail.accumulatedFuelUsedL, 'l', 2) }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.columns.runtime' | translate }}</div>
              <div class="fw-semibold">{{ formatNumber(tripDetail.accumulatedRuntimeMin, 'min') }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.driving-time' | translate }}</div>
              <div class="fw-semibold">{{ formatNumber(statValue(tripDetail.drivingTimeMin), 'min') }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.average-fuel-consumption' | translate }}</div>
              <div class="fw-semibold">{{ formatNumber(statValue(tripDetail.averageFuelConsumptionL100km), 'l/100km') }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.fuel-efficiency' | translate }}</div>
              <div class="fw-semibold">{{ formatNumber(statValue(tripDetail.fuelEfficiencyKmPerL), 'km/l') }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.idle-time' | translate }}</div>
              <div class="fw-semibold">{{ formatNumber(statValue(tripDetail.idleTimeMin), 'min') }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.engine-warm-up-time' | translate }}</div>
              <div class="fw-semibold">{{ formatNumber(statValue(tripDetail.engineWarmUpTimeMin), 'min') }}</div>
            </div>
          </div>
        </section>

        <section class="mb-4">
          <h6 class="text-uppercase text-body-secondary small mb-3">{{ 'i18n.domain.analytics.trip-overview.detail.section-engine' | translate }}</h6>
          <div class="row g-3">
            <div class="col-6 col-md-4">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.engine-load-min' | translate }}</div>
              <div class="fw-semibold">{{ formatNumber(statValue(tripDetail.engineLoadMinPercent), '%') }}</div>
            </div>
            <div class="col-6 col-md-4">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.engine-load-avg' | translate }}</div>
              <div class="fw-semibold">{{ formatNumber(statValue(tripDetail.engineLoadAvgPercent), '%') }}</div>
            </div>
            <div class="col-6 col-md-4">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.engine-load-max' | translate }}</div>
              <div class="fw-semibold">{{ formatNumber(statValue(tripDetail.engineLoadMaxPercent), '%') }}</div>
            </div>
          </div>

          <div class="mt-3">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <span class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.engine-load-histogram' | translate }}</span>
              @if (isUnavailable(tripDetail.engineLoadHistogram)) {
                <span class="badge text-bg-secondary" [title]="unavailableTooltip(tripDetail.engineLoadHistogram)">{{ 'i18n.domain.analytics.trip-overview.detail.unavailable' | translate }}</span>
              }
            </div>
            @if (!isUnavailable(tripDetail.engineLoadHistogram)) {
              <ejs-chart
                class="engine-load-histogram-chart"
                [theme]="chartTheme"
                [primaryXAxis]="engineLoadHistogramPrimaryXAxis"
                [primaryYAxis]="engineLoadHistogramPrimaryYAxis"
                [tooltip]="engineLoadHistogramTooltip"
                [series]="engineLoadHistogramSeries"
                [legendSettings]="engineLoadHistogramLegend"
                background="transparent"
                height="220px"
                width="100%">
              </ejs-chart>
            }
          </div>
        </section>

        <section>
          <h6 class="text-uppercase text-body-secondary small mb-3">{{ 'i18n.domain.analytics.trip-overview.detail.section-driver-behavior' | translate }}</h6>

          <div class="d-flex flex-wrap gap-2 mb-3">
            @if (isUnavailable(tripDetail.tripClassification)) {
              <span class="badge text-bg-secondary" [title]="unavailableTooltip(tripDetail.tripClassification)">{{ 'i18n.domain.analytics.trip-overview.detail.unavailable' | translate }}</span>
            } @else {
              <span class="badge text-bg-info">{{ statValue(tripDetail.tripClassification) }}</span>
            }

            @if (isUnavailable(tripDetail.isShortTrip)) {
              <span class="badge text-bg-secondary" [title]="unavailableTooltip(tripDetail.isShortTrip)">
                {{ 'i18n.domain.analytics.trip-overview.detail.is-short-trip' | translate }}: {{ 'i18n.domain.analytics.trip-overview.detail.unavailable' | translate }}
              </span>
            } @else {
              <span class="badge" [class]="boolBadgeClass(statValue(tripDetail.isShortTrip), true)">
                {{ 'i18n.domain.analytics.trip-overview.detail.is-short-trip' | translate }}: {{ formatBoolean(statValue(tripDetail.isShortTrip)) }}
              </span>
            }

            @if (isUnavailable(tripDetail.coldStart)) {
              <span class="badge text-bg-secondary" [title]="unavailableTooltip(tripDetail.coldStart)">
                {{ 'i18n.domain.analytics.trip-overview.detail.cold-start' | translate }}: {{ 'i18n.domain.analytics.trip-overview.detail.unavailable' | translate }}
              </span>
            } @else {
              <span class="badge" [class]="boolBadgeClass(statValue(tripDetail.coldStart), true)">
                {{ 'i18n.domain.analytics.trip-overview.detail.cold-start' | translate }}: {{ formatBoolean(statValue(tripDetail.coldStart)) }}
              </span>
            }
          </div>

          <div class="mb-3">
            <div class="d-flex justify-content-between align-items-center">
              <span class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.driver-score' | translate }}</span>
              @if (isUnavailable(tripDetail.driverScore)) {
                <span class="badge text-bg-secondary" [title]="unavailableTooltip(tripDetail.driverScore)">{{ 'i18n.domain.analytics.trip-overview.detail.unavailable' | translate }}</span>
              } @else {
                <span class="fw-semibold">{{ formatNumber(statValue(tripDetail.driverScore), '', 0) }}</span>
              }
            </div>
            <div class="progress" role="progressbar" [attr.aria-valuenow]="driverScorePercent(statValue(tripDetail.driverScore))" aria-valuemin="0" aria-valuemax="100">
              <div class="progress-bar" [class]="driverScoreBarClass(statValue(tripDetail.driverScore))" [style.width.%]="driverScorePercent(statValue(tripDetail.driverScore))"></div>
            </div>
          </div>

          <div class="row g-3">
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.harsh-braking' | translate }}</div>
              @if (isUnavailable(tripDetail.harshBrakingCount)) {
                <span class="badge text-bg-secondary" [title]="unavailableTooltip(tripDetail.harshBrakingCount)">{{ 'i18n.domain.analytics.trip-overview.detail.unavailable' | translate }}</span>
              } @else {
                <span class="badge" [class]="eventCountBadgeClass(statValue(tripDetail.harshBrakingCount))">{{ formatCount(statValue(tripDetail.harshBrakingCount)) }}</span>
              }
            </div>
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.harsh-acceleration' | translate }}</div>
              @if (isUnavailable(tripDetail.harshAccelerationCount)) {
                <span class="badge text-bg-secondary" [title]="unavailableTooltip(tripDetail.harshAccelerationCount)">{{ 'i18n.domain.analytics.trip-overview.detail.unavailable' | translate }}</span>
              } @else {
                <span class="badge" [class]="eventCountBadgeClass(statValue(tripDetail.harshAccelerationCount))">{{ formatCount(statValue(tripDetail.harshAccelerationCount)) }}</span>
              }
            </div>
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.aggressive-cornering' | translate }}</div>
              @if (isUnavailable(tripDetail.aggressiveCorneringCount)) {
                <span class="badge text-bg-secondary" [title]="unavailableTooltip(tripDetail.aggressiveCorneringCount)">{{ 'i18n.domain.analytics.trip-overview.detail.unavailable' | translate }}</span>
              } @else {
                <span class="badge" [class]="eventCountBadgeClass(statValue(tripDetail.aggressiveCorneringCount))">{{ formatCount(statValue(tripDetail.aggressiveCorneringCount)) }}</span>
              }
            </div>
            <div class="col-6 col-md-3">
              <div class="text-body-secondary small">{{ 'i18n.domain.analytics.trip-overview.detail.speeding' | translate }}</div>
              @if (isUnavailable(tripDetail.speedingCount)) {
                <span class="badge text-bg-secondary" [title]="unavailableTooltip(tripDetail.speedingCount)">{{ 'i18n.domain.analytics.trip-overview.detail.unavailable' | translate }}</span>
              } @else {
                <span class="badge" [class]="eventCountBadgeClass(statValue(tripDetail.speedingCount))">{{ formatCount(statValue(tripDetail.speedingCount)) }}</span>
              }
            </div>
          </div>
        </section>

      </div>
    }
  </ng-template>
</ejs-dialog>
